import React, { useState, useEffect } from "react";
import Banco from "../../components/bank";
import { StyledButtonSalvar } from "../../components/default_button/styles";
import {
  MainContainer,
  ContentWrapper,
  Row,
  Label,
  ShowInput,
  FormContainer,
  LeftColumn,
  RightColumn,
  ButtonContainer,
  Subtitle,
  Divider,
  StyledSelect,
} from "./styles";
import { useLocation, useNavigate } from "react-router-dom";
import { appStatus } from "../../store/modules/app_status/actions";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";
import { useHookFormMask } from "use-mask-input";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ErrorForm } from "../../components/inter_register_user/ErrorForm";
import { registerUserSchema } from "../../components/inter_register_user/registerUserValidation";
import WarningDeleteModal from '../../components/warning_delete_modal';
import UserProjects from "./userProjects/index";
import { projectService } from "../../services/projectService";
import { userService } from "../../services/userService";
import { handleCepChange } from "../../api/requests/cep";
import PageHeader from "../../components/page_header";
import { goToPanel } from "../../utils/appNavigation";
import { canEditUserRecord, canManageUsers, isEngineeringUser, isPropOrGuardianUser } from "../../utils/userRoles";

const UserIntern = ({ user: userProp } = {}) => {
  const location = useLocation();
  const [showModalBanco, setShowModalBanco] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.user.currentUser);
  const [user, setUser] = useState(null);
  const [userProjects, setUserProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const fromProfile = location.state?.from === "profile";
  const canManage = canManageUsers(currentUser);
  const canEditTarget =
    fromProfile || canEditUserRecord(currentUser, user || location.state?.user);
  const canEditForm = canEditTarget;
  const canDeleteUser = !fromProfile && canManage;
  const engCanToggleFieldType =
    !fromProfile &&
    isEngineeringUser(currentUser) &&
    !canManage &&
    isPropOrGuardianUser(user || location.state?.user);

  const userId =
    location.state?.user?.id || location.state?.userId || userProp?.id;

  const {
    register,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm({
    resolver: zodResolver(registerUserSchema),
    defaultValues: {
      id: "",
      full_name: "",
      rg: "",
      cpf: "",
      cnpj: "",
      phone: "",
      email: "",
      user_type: "",
      cep: "",
      street: "",
      number: "",
      complement: "",
      district: "",
      state: "",
      city: "",
    },
  });

  const registerWithMask = useHookFormMask(register);

  useEffect(() => {
    if (!userId) return;

    let cancelled = false;

    const applyUserToForm = (userData) => {
      setUser(userData);
      reset({
        id: userData.id,
        full_name: userData.full_name ?? "",
        email: userData.email ?? "",
        user_type: userData.user_type ?? "",
        cep: userData.cep ?? "",
        street: userData.street ?? "",
        number: userData.number ?? "",
        complement: userData.complement ?? "",
        district: userData.district ?? "",
        state: userData.state ?? "",
        city: userData.city ?? "",
        rg: userData.rg ?? "",
        cpf: userData.cpf ?? "",
        cnpj: userData.cnpj ?? "",
        phone: userData.phone ?? "",
      });
    };

    const fetchUser = async () => {
      try {
        const userData = await userService.getUserById(userId);
        if (cancelled) return;
        applyUserToForm(userData);
      } catch (error) {
        console.error("Erro ao buscar usuário:", error);
        if (cancelled) return;

        // Fallback: dados já vindos da navegação (lista / perfil)
        const fallback = location.state?.user || userProp;
        if (fallback?.id) {
          applyUserToForm(fallback);
          return;
        }

        Swal.fire({
          title: "Erro!",
          text: "Não foi possível carregar os dados do usuário.",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    };

    fetchUser();
    return () => {
      cancelled = true;
    };
    // Só recarrega quando muda o id — evita resetar o form a cada re-render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, reset]);


  useEffect(() => {
    if (fromProfile) return;

    const fetchUserProjects = async () => {
      if (!user?.id) return;

      setLoadingProjects(true);
      try {
        const response = await projectService.getProjectByUserId(user.id);
        setUserProjects(response);
      } catch (error) {
        console.error("Erro ao carregar projetos do usuário:", error);
        Swal.fire({
          title: "Erro!",
          text: "Não foi possível carregar os projetos do usuário.",
          icon: "error",
          confirmButtonText: "OK",
        });
      } finally {
        setLoadingProjects(false);
      }
    };

    fetchUserProjects();
  }, [user?.id, fromProfile]);

  async function lookupCep(rawCEP) {
    const cep = String(rawCEP || "").replace(/\D/g, "");

    if (cep.length !== 8) {
      console.warn("CEP inválido");
      return;
    }

    try {
      const data = await handleCepChange(cep);

      setValue("street", data.street || data.logradouro || "");
      setValue("district", data.neighborhood || data.bairro || "");
      setValue("city", data.city || data.localidade || "");
      setValue("state", data.state || data.uf || "");
    } catch (error) {
      console.error("Erro ao buscar CEP:", error);
    }
  }

  async function handleCEPBlur(e) {
    await lookupCep(e.target.value);
  }

  /** Enter no CEP busca o endereço — não dispara Salvar do formulário. */
  function handleCEPKeyDown(e) {
    if (e.key !== "Enter") return;
    e.preventDefault();
    lookupCep(e.target.value);
  }
  const handleModalBanco = () => {
    setShowModalBanco(!showModalBanco);
  };

  const handleRegister = async (event) => {
    event?.preventDefault?.();

    if (!canEditForm) {
      return;
    }

    try {
      const formValues = watch();
      if (!formValues?.id) {
        Swal.fire({
          title: "Erro!",
          text: "Usuário não carregado. Volte e abra o cadastro novamente.",
          icon: "error",
          confirmButtonText: "OK",
        });
        return;
      }

      // Payload enxuto — evita enviar campos extras/readonly que o backend rejeita
      const userUpdate = {
        id: formValues.id,
        full_name: formValues.full_name,
        rg: formValues.rg,
        cpf: formValues.cpf,
        phone: formValues.phone,
        cep: formValues.cep,
        cnpj: formValues.cnpj,
        street: formValues.street,
        number: formValues.number,
        district: formValues.district,
        complement: formValues.complement,
        city: formValues.city,
        state: formValues.state,
        email: formValues.email,
      };

      // ADM: qualquer user_type. ENG: só PROP ↔ GUARDIAN. Perfil próprio: nunca.
      if (!fromProfile && formValues.user_type) {
        if (canManage) {
          userUpdate.user_type = formValues.user_type;
        } else if (
          engCanToggleFieldType &&
          (formValues.user_type === "PROP" || formValues.user_type === "GUARDIAN")
        ) {
          userUpdate.user_type = formValues.user_type;
        }
      }

      await userService.updateUser(formValues.id, userUpdate);

      const freshUser = await userService.getUserById(formValues.id);
      setUser(freshUser);
      reset(freshUser);

      Swal.fire({
        title: "Sucesso!",
        text: "Usuário atualizado com sucesso!",
        icon: "success",
        confirmButtonText: "OK",
      });

      if (fromProfile) {
        goToPanel(dispatch, navigate, "Meu Perfil", { replace: true });
      } else {
        goToPanel(dispatch, navigate, "Usuários", { replace: true });
      }
    } catch (error) {
      console.error("Erro ao atualizar usuário:", error);

      const status = error?.response?.status;
      const detail =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        (Array.isArray(error?.response?.data)
          ? error.response.data.join(" ")
          : null);

      let text = "Algo deu errado ao tentar processar sua requisição.";
      if (status === 403) {
        text =
          detail ||
          "Sem permissão para editar este usuário. Se for o seu perfil, o backend precisa permitir autoedição (deploy da API).";
      } else if (status === 400 && detail) {
        text = typeof detail === "string" ? detail : JSON.stringify(detail);
      } else if (detail && typeof detail === "string") {
        text = detail;
      }

      Swal.fire({
        title: "Erro!",
        text,
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };


  const handleComeBack = () => {
    if (fromProfile) {
      goToPanel(dispatch, navigate, "Meu Perfil", { replace: true });
      return;
    }
    goToPanel(dispatch, navigate, "Usuários", { replace: true });
  };

  const sendInternProject = (project) => {
    dispatch(appStatus(""));
    navigate(`/intern_project/${project.id}`, { state: { project, user } });
  };

  return (
    <MainContainer>
      <ContentWrapper>
        <PageHeader
          title="Informações Cadastrais"
          actionLabel="Voltar"
          onAction={handleComeBack}
        />
        <FormContainer onSubmit={handleRegister}>
          <Subtitle>📌 Dados Pessoais</Subtitle>
          <LeftColumn>
            <Row>
              <Label>Nome completo:</Label>
              <ShowInput
                type="text"
                id="full_name"
                {...register("full_name")}
              />
              <ErrorForm message={errors.full_name?.message} />
            </Row>
            <Row>
              <Label>Email:</Label>
              <ShowInput type="email" id="email" {...register("email")} />
              <ErrorForm message={errors.email?.message} />
            </Row>
            {(canManage || engCanToggleFieldType) && (
              <Row>
                <Label>Tipo de acesso:</Label>
                {canManage ? (
                  <StyledSelect id="user_type" {...register("user_type")}>
                    <option value="ADM">ADM</option>
                    <option value="ENG">ENG</option>
                    <option value="COM">COM</option>
                    <option value="Regular">Regular</option>
                    <option value="PROP">PROP</option>
                    <option value="GUARDIAN">GUARDIAN</option>
                  </StyledSelect>
                ) : (
                  <StyledSelect id="user_type" {...register("user_type")}>
                    <option value="PROP">PROP</option>
                    <option value="GUARDIAN">GUARDIAN</option>
                  </StyledSelect>
                )}
              </Row>
            )}
            {!canManage && !engCanToggleFieldType && user?.user_type && (
              <Row>
                <Label>Tipo de acesso:</Label>
                <ShowInput type="text" value={user.user_type} disabled readOnly />
              </Row>
            )}
            <Row>
              <Label>Whatsapp:</Label>
              <ShowInput
                key={`phone-${user?.id || "new"}`}
                type="text"
                id="phone"
                {...registerWithMask("phone", "(99) 99999-9999")}
              />
              <ErrorForm message={errors.phone?.message} />
            </Row>
          </LeftColumn>
          <RightColumn>
            <Row>
              <Label htmlFor="rg">RG:</Label>
              <ShowInput
                key={`rg-${user?.id || "new"}`}
                type="text"
                id="rg"
                {...registerWithMask("rg", "99.999.999-9")}
              />
              <ErrorForm message={errors.rg?.message} />
            </Row>
            <Row>
              <Label htmlFor="cpf">CPF:</Label>
              <ShowInput
                key={`cpf-${user?.id || "new"}`}
                type="text"
                id="cpf"
                {...registerWithMask("cpf", "999.999.999-99")}
              />
              <ErrorForm message={errors.cpf?.message} />
            </Row>
            <Row>
              <Label htmlFor="cnpj">CNPJ:</Label>
              <ShowInput
                key={`cnpj-${user?.id || "new"}`}
                type="text"
                id="cnpj"
                {...registerWithMask("cnpj", "99.999.999/9999-99")}
              />
              <ErrorForm message={errors.cnpj?.message} />
            </Row>
          </RightColumn>
          <Divider />
          <Subtitle>🏠 Endereço</Subtitle>
          <LeftColumn>
            <Row>
              <Label htmlFor="cep">CEP:</Label>
              <ShowInput
                key={`cep-${user?.id || "new"}`}
                type="text"
                id="cep"
                {...registerWithMask("cep", "99999-999", {
                  onBlur: handleCEPBlur,
                  onKeyDown: handleCEPKeyDown,
                })}
              />
              <ErrorForm message={errors.cep?.message} />
            </Row>
            <Row>
              <Label htmlFor="rua">Rua:</Label>
              <ShowInput
                type="text"
                id="street"
                {...register("street")}
                placeholder="Preenchido pelo CEP — pode editar se precisar"
              />
              <ErrorForm message={errors.street?.message} />
            </Row>
            <Row>
              <Label htmlFor="bairro">Bairro:</Label>
              <ShowInput
                type="text"
                id="district"
                {...register("district")}
                placeholder="Preenchido pelo CEP — pode editar se precisar"
              />
              <ErrorForm message={errors.district?.message} />
            </Row>
            <Row>
              <Label htmlFor="cidade">Cidade:</Label>
              <ShowInput
                type="text"
                id="city"
                {...register("city")}
                placeholder="Preenchido pelo CEP — pode editar se precisar"
              />
              <ErrorForm message={errors.city?.message} />
            </Row>
          </LeftColumn>
          <RightColumn>
            <Row>
              <Label htmlFor="uf">UF:</Label>
              <ShowInput
                type="text"
                id="state"
                {...register("state")}
                placeholder="Preenchido pelo CEP — pode editar se precisar"
              />
              <ErrorForm message={errors.state?.message} />
            </Row>
            <Row>
              <Label htmlFor="numero">Número:</Label>
              <ShowInput type="text" id="number" {...register("number")} />
              <ErrorForm message={errors.number?.message} />
            </Row>
            <Row>
              <Label htmlFor="complemento">Complemento:</Label>
              <ShowInput
                type="text"
                id="complement"
                {...register("complement")}
              />
              <ErrorForm message={errors.complement?.message} />
            </Row>
          </RightColumn>

          <ButtonContainer>
            {canDeleteUser && (
              <WarningDeleteModal
                text={"Deletar Usuário"}
                path={"users"}
                id={user?.id}
                context={
                  String(currentUser?.id) === String(user?.id)
                    ? "admin"
                    : "self"
                }
              />
            )}
            {canEditForm && (
              <StyledButtonSalvar type="submit">
                Salvar
              </StyledButtonSalvar>
            )}
          </ButtonContainer>
        </FormContainer>

        {!fromProfile && canManage && (
          <ButtonContainer style={{ color: 'var(--danger)' }}>
            {showModalBanco && (
              <Banco isOpen={showModalBanco} onClose={handleModalBanco} />
            )}

            <UserProjects
              loadingProjects={loadingProjects}
              userProjects={userProjects}
              onProjectClick={sendInternProject}
            />
          </ButtonContainer>
        )}
        {fromProfile && showModalBanco && (
          <Banco isOpen={showModalBanco} onClose={handleModalBanco} />
        )}
      </ContentWrapper>
    </MainContainer>
  );
};

export default UserIntern;